import { useState, useMemo } from 'react';
import { FiCopy, FiCheck } from 'react-icons/fi';

interface CodeBlockProps {
  code: string;
  language?: string;
  filename?: string;
}

function highlightSyntax(line: string): JSX.Element[] {
  let remaining = line;
  const tokens: Array<{ type: string; text: string }> = [];

  // Comments (take precedence)
  const commentMatch = remaining.match(/(#.*$)/);
  if (commentMatch) {
    const idx = remaining.indexOf(commentMatch[0]);
    if (idx > 0) {
      tokens.push({ type: 'text', text: remaining.substring(0, idx) });
    }
    tokens.push({ type: 'comment', text: commentMatch[0] });
    remaining = '';
  }

  if (remaining) {
    let lastIndex = 0;
    // Strings (triple-quoted, f-strings, then regular)
    const stringRegex = /("""[\s\S]*?"""|'''[\s\S]*?'''|f"[^"]*"|f'[^']*'|"[^"]*"|'[^']*')/g;
    let match;
    while ((match = stringRegex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        tokens.push({ type: 'text', text: remaining.substring(lastIndex, match.index) });
      }
      tokens.push({ type: 'string', text: match[0] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) {
      tokens.push({ type: 'text', text: remaining.substring(lastIndex) });
    }
  }

  if (tokens.length === 0) {
    return [<span key={0}>{line}</span>];
  }

  return tokens.map((token, idx) => {
    if (token.type === 'comment') {
      return <span key={idx} className="text-gray-500">{token.text}</span>;
    }
    if (token.type === 'string') {
      return <span key={idx} className="text-green-400">{token.text}</span>;
    }
    // Highlight keywords, builtins, decorators, and numbers
    const text = token.text;
    const parts: JSX.Element[] = [];
    let lastIdx = 0;
    const kwRegex = /(@\w+|\b(def|class|if|elif|else|for|while|return|import|from|as|try|except|with|in|and|or|not|True|False|None|self|raise|yield|async|await|lambda|pass)\b|\b\d+[\d_.]*\b)/g;
    let kwMatch;
    while ((kwMatch = kwRegex.exec(text)) !== null) {
      if (kwMatch.index > lastIdx) {
        parts.push(<span key={`${idx}-${parts.length}`}>{text.substring(lastIdx, kwMatch.index)}</span>);
      }
      const matched = kwMatch[0];
      if (matched.startsWith('@')) {
        parts.push(<span key={`${idx}-${parts.length}`} className="text-yellow-400">{matched}</span>);
      } else if (/^\d/.test(matched)) {
        parts.push(<span key={`${idx}-${parts.length}`} className="text-amber-400">{matched}</span>);
      } else {
        parts.push(<span key={`${idx}-${parts.length}`} className="text-purple-400">{matched}</span>);
      }
      lastIdx = kwMatch.index + matched.length;
    }
    if (lastIdx < text.length) {
      parts.push(<span key={`${idx}-${parts.length}`}>{text.substring(lastIdx)}</span>);
    }
    return <span key={idx}>{parts.length > 0 ? parts : text}</span>;
  });
}

function highlightBash(line: string): JSX.Element[] {
  const tokens: JSX.Element[] = [];
  let lastIdx = 0;
  // Highlight $ prompt, flags, strings, and comments
  const bashRegex = /(\$\s|#.*$|"[^"]*"|'[^']*'|\s--?\w[\w-]*)/g;
  let match;
  while ((match = bashRegex.exec(line)) !== null) {
    if (match.index > lastIdx) {
      tokens.push(<span key={tokens.length}>{line.substring(lastIdx, match.index)}</span>);
    }
    const m = match[0];
    if (m === '$ ') {
      tokens.push(<span key={tokens.length} className="text-green-400">{m}</span>);
    } else if (m.startsWith('#')) {
      tokens.push(<span key={tokens.length} className="text-gray-500">{m}</span>);
    } else if (m.startsWith('"') || m.startsWith("'")) {
      tokens.push(<span key={tokens.length} className="text-green-400">{m}</span>);
    } else {
      tokens.push(<span key={tokens.length} className="text-sky-400">{m}</span>);
    }
    lastIdx = match.index + m.length;
  }
  if (lastIdx < line.length) {
    tokens.push(<span key={tokens.length}>{line.substring(lastIdx)}</span>);
  }
  return tokens.length > 0 ? tokens : [<span key={0}>{line}</span>];
}

export default function CodeBlock({ code, language = 'python', filename }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const lines = useMemo(() => code.split('\n'), [code]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const highlighter = language === 'bash' ? highlightBash : highlightSyntax;

  return (
    <div className="text-left bg-gray-950 border border-gray-800 rounded-lg overflow-hidden my-4">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900/80 border-b border-gray-800">
        <span className="text-xs text-gray-500 font-mono">
          {filename || language}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
        >
          {copied ? (
            <>
              <FiCheck className="w-3.5 h-3.5 text-green-400" />
              <span className="text-green-400">Copied</span>
            </>
          ) : (
            <>
              <FiCopy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>

      {/* Code area */}
      <div className="overflow-x-auto text-left">
        <pre className="p-4 text-sm font-mono leading-relaxed text-left">
          <code className="text-left text-gray-300">
            {lines.map((line, idx) => (
              <div key={idx} className="text-left">
                {highlighter(line)}
              </div>
            ))}
          </code>
        </pre>
      </div>
    </div>
  );
}
